// src/providers/authProvider.ts
import { AuthBindings } from "@refinedev/core";
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../utils/firebase";

// Log Firebase auth state on initialization
console.log("Firebase auth initialized:", auth);
console.log("Firebase auth app:", auth.app);

// Cache for impersonated user data
let impersonatedUserCache: any = null;
let lastImpersonatedUid: string | null = null;

// Export helper function to get current user token
export const getCurrentUserToken = async (): Promise<string | null> => {
  try {
    if (auth.currentUser) {
      const token = await auth.currentUser.getIdToken();
      return token;
    }
    return null;
  } catch (error) {
    console.error("Error getting user token:", error);
    return null;
  }
};

/**
 * Builds API URL with impersonation support
 * @param endpoint - The API endpoint (e.g., '/user-jobs')
 * @param params - Optional URL parameters
 * @returns Complete URL with impersonation parameter if applicable
 */
export const buildApiUrl = (endpoint: string, params?: Record<string, string>): string => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  let url = `${baseUrl}${endpoint}`;
  
  // Check for impersonation
  const impersonateUid = localStorage.getItem('impersonateUid');
  
  // Build query parameters
  const urlParams = new URLSearchParams();
  
  // Add impersonation if active
  if (impersonateUid) {
    urlParams.append('impersonate', impersonateUid);
  }
  
  // Add any additional parameters
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      urlParams.append(key, value);
    });
  }
  
  // Append parameters if any exist
  const queryString = urlParams.toString();
  if (queryString) {
    url += `?${queryString}`;
  }
  
  return url;
};

/**
 * Makes an authenticated API request with impersonation support
 * @param endpoint - The API endpoint
 * @param options - Fetch options
 * @returns Response from the API
 */
export const authenticatedFetch = async (
  endpoint: string, 
  options?: RequestInit & { params?: Record<string, string> }
): Promise<Response> => {
  const token = await getCurrentUserToken();
  
  if (!token) {
    throw new Error('Authentication required. Please log in again.');
  }
  
  const { params, ...fetchOptions } = options || {};
  const url = buildApiUrl(endpoint, params);
  
  return fetch(url, {
    ...fetchOptions,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...fetchOptions?.headers,
    },
  });
};

/**
 * Adds impersonation parameter to FormData if active
 * @param formData - The FormData object to modify
 * @returns The same FormData object with impersonation added if applicable
 */
export const addImpersonationToFormData = (formData: FormData): FormData => {
  const impersonateUid = localStorage.getItem('impersonateUid');
  if (impersonateUid) {
    formData.append('impersonate', impersonateUid);
  }
  return formData;
};

// Helper function to get user data (handles impersonation)
const getUserData = async (uid: string) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        id: uid,
        name: userData.displayName || userData.name || userData.email,
        email: userData.email,
        avatar: null,
        plan: userData.planType || 'free',
        isAdmin: userData.isAdmin || false,
        defaultAIInstructions: userData.defaultAIInstructions || '',
        company: userData.company || '',
        companyLogo: userData.companyLogo || null,
        usage: {
          videosThisMonth: userData.usage?.videosThisMonth || 0,
          scriptsThisMonth: userData.usage?.scriptsThisMonth || 0,
          videosAllTime: userData.usage?.videosAllTime || 0,
          scriptsAllTime: userData.usage?.scriptsAllTime || 0,
          lastVideoCreated: userData.usage?.lastVideoCreated || null,
          lastScriptCreated: userData.usage?.lastScriptCreated || null
        },
        // Add impersonation metadata
        isImpersonated: uid !== auth.currentUser?.uid,
        actualUserId: auth.currentUser?.uid
      };
    }
  } catch (error) {
    console.error("Error fetching user data:", error);
  }
  return null;
};

export const authProvider: AuthBindings = {
  login: async ({ email, password, onError }) => {
    try {
      // Check if Firebase is properly initialized
      if (!auth) {
        throw new Error("Firebase authentication is not initialized. Please check your configuration.");
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      if (user) {
        // Update last login
        await setDoc(doc(db, 'users', user.uid), {
          lastLogin: new Date()
        }, { merge: true });
        
        // Clear any impersonation on login
        localStorage.removeItem('impersonateUid');
        impersonatedUserCache = null;
        lastImpersonatedUid = null;
        
        return {
          success: true,
          redirectTo: "/dashboard",
        };
      }
    } catch (error: any) {
      console.error("Login error:", error);
      
      // Map Firebase error codes to user-friendly messages
      let errorMessage = error.message;
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      } else if (error.code === 'auth/configuration-not-found') {
        errorMessage = 'Firebase configuration error. Please contact support.';
      }
      
      return {
        success: false,
        error: {
          name: "LoginError",
          message: errorMessage,
        },
      };
    }

    return {
      success: false,
      error: {
        name: "LoginError",
        message: "Login failed",
      },
    };
  },

  register: async ({ email, password, name, plan = 'free' }) => {
    console.log('account register...');
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      if (user) {
        // Create user document in Firestore
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          displayName: name,
          planType: plan,
          planStatus: 'active',
          isAdmin: false,
          usage: {
            videosThisMonth: 0,
            scriptsThisMonth: 0,
            videosAllTime: 0,
            scriptsAllTime: 0,
            lastVideoCreated: null,
            lastScriptCreated: null
          },
          createdAt: new Date(),
          updatedAt: new Date()
        });
        
        console.log('creating GHL contact...');

        // Create GHL contact AFTER user document is created
        try {
          const token = await user.getIdToken();
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/create-ghl-contact`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              uid: user.uid,
              email: user.email,
              name: name,
              planType: plan
            }),
          });
          
          if (!response.ok) {
            console.error('Failed to create GHL contact:', await response.text());
          }
        } catch (ghlError) {
          console.error('Failed to create GHL contact:', ghlError);
          // Don't fail registration if GHL fails
        }
  
        return {
          success: true,
          redirectTo: "/dashboard",
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "RegisterError",
          message: error.message,
        },
      };
    }
  
    return {
      success: false,
      error: {
        name: "RegisterError",
        message: "Registration failed",
      },
    };
  },

  logout: async () => {
    try {
      // Clear impersonation on logout
      localStorage.removeItem('impersonateUid');
      impersonatedUserCache = null;
      lastImpersonatedUid = null;
      
      await signOut(auth);
      
      // Force a full page reload to go to home page
      window.location.href = 'https://spectreviews.com';
      
      return {
        success: true,
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "LogoutError",
          message: error.message,
        },
      };
    }
  },

  check: () => {
    return new Promise((resolve) => {
      onAuthStateChanged(auth, (user: User | null) => {
        if (user) {
          resolve({
            authenticated: true,
          });
        } else {
          // Clear impersonation if not authenticated
          localStorage.removeItem('impersonateUid');
          impersonatedUserCache = null;
          lastImpersonatedUid = null;
          
          resolve({
            authenticated: false,
            logout: true,
            redirectTo: "/",
          });
        }
      });
    });
  },

  getPermissions: async () => {
    const user = auth.currentUser;
    if (user) {
      // Check for impersonation
      const impersonateUid = localStorage.getItem('impersonateUid');
      const targetUid = impersonateUid || user.uid;
      
      const userDoc = await getDoc(doc(db, 'users', targetUid));
      const userData = userDoc.data();
      return userData?.planType || 'free';
    }
    return null;
  },

  getIdentity: async () => {
    const user = auth.currentUser;
    if (!user) return null;

    try {
      // Check for impersonation
      const impersonateUid = localStorage.getItem('impersonateUid');
      
      if (impersonateUid) {
        // Check cache first
        if (impersonateUid === lastImpersonatedUid && impersonatedUserCache) {
          console.log('Returning cached impersonated user data');
          return impersonatedUserCache;
        }
        
        // Fetch impersonated user data
        console.log('Fetching impersonated user data for:', impersonateUid);
        const impersonatedData = await getUserData(impersonateUid);
        
        if (impersonatedData) {
          // Update cache
          impersonatedUserCache = impersonatedData;
          lastImpersonatedUid = impersonateUid;
          return impersonatedData;
        } else {
          // If impersonated user not found, clear impersonation
          console.error('Impersonated user not found, clearing impersonation');
          localStorage.removeItem('impersonateUid');
          impersonatedUserCache = null;
          lastImpersonatedUid = null;
        }
      } else {
        // Clear cache if not impersonating
        if (impersonatedUserCache) {
          impersonatedUserCache = null;
          lastImpersonatedUid = null;
        }
      }
      
      // Return actual user data
      const userData = await getUserData(user.uid);
      const defaultAIInstructions = userData?.defaultAIInstructions || '';

      return userData || {
        id: user.uid,
        name: user.email,
        email: user.email,
        avatar: null,
        plan: 'free',
        isAdmin: false,
        defaultAIInstructions: defaultAIInstructions,
        usage: {
          videosThisMonth: 0,
          scriptsThisMonth: 0,
          videosAllTime: 0,
          scriptsAllTime: 0,
          lastVideoCreated: null,
          lastScriptCreated: null
        }
      };
      
    } catch (error) {
      console.error("Error fetching user identity:", error);
      
      // Return default data if fetch fails
      return {
        id: user.uid,
        name: user.email,
        email: user.email,
        avatar: null,
        plan: 'free',
        isAdmin: false,
        usage: {
          videosThisMonth: 0,
          scriptsThisMonth: 0,
          videosAllTime: 0,
          scriptsAllTime: 0,
          lastVideoCreated: null,
          lastScriptCreated: null
        }
      };
    }
  },
  
  onError: async (error) => {
    console.error('Auth error:', error);
    
    if (error?.status === 401) {
      // Unauthorized - redirect to login
      return {
        logout: true,
        redirectTo: "/login",
        error: {
          name: "Authentication Error",
          message: "You need to login to access this page",
        },
      };
    }
    
    return {};
  },
};

// Helper function to clear impersonation cache (useful when usage is updated)
export const clearImpersonationCache = () => {
  impersonatedUserCache = null;
};